import React from "react";
import { FaClipboardList, FaSyncAlt } from "react-icons/fa";
import type { TFunction } from "i18next";

interface MutationRequestsHeaderProps {
  t: TFunction;
  pendingCount: number;
  refreshing: boolean;
  onRefresh: () => void;
}

export const MutationRequestsHeader = ({
  t,
  pendingCount,
  refreshing,
  onRefresh,
}: MutationRequestsHeaderProps) => {
  const pageTitle = t("mutations.title", { defaultValue: "Mutation requests" });

  return (
    <header className="lds-section-enter relative overflow-hidden rounded-[30px] border border-indigo-100/80 bg-gradient-to-r from-indigo-50/90 via-white to-sky-50/75 p-6 shadow-[0_24px_70px_rgba(67,56,202,0.12)] dark:border-indigo-400/20 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/20 sm:p-8">
      <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[38%] opacity-75 lg:block">
        <div className="absolute bottom-0 right-0 h-20 w-full rounded-tl-[80px] bg-sky-100/75 dark:bg-sky-300/10" />
        <div className="absolute bottom-0 right-20 h-36 w-44 rounded-t-full bg-indigo-100/75 dark:bg-indigo-300/10" />
        <div className="absolute bottom-4 right-12 grid grid-cols-6 gap-2 opacity-60">
          {Array.from({ length: 30 }).map((_, index) => (
            <span key={index} className="h-1.5 w-1.5 rounded-full bg-indigo-300/70 dark:bg-indigo-200/30" />
          ))}
        </div>
      </div>

      <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-5">
          <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-violet-200 to-sky-50 text-3xl text-violet-700 shadow-lg dark:border-slate-900 dark:from-violet-800 dark:to-sky-950 dark:text-violet-100 sm:h-24 sm:w-24">
            <FaClipboardList aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <h1 className="text-3xl font-bold text-slate-950 dark:text-white sm:text-4xl">{pageTitle}</h1>
              {pendingCount > 0 && (
                <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200">
                  {t("mutations.pending_count", {
                    count: pendingCount,
                    defaultValue: "{{count}} pending",
                  })}
                </span>
              )}
            </div>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              {t("mutations.subtitle", {
                defaultValue: "Review, approve and manage submitted data changes.",
              })}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="inline-flex items-center justify-center gap-2 self-start rounded-full border border-indigo-600 bg-indigo-600 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-indigo-900/15 transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 sm:self-auto dark:border-indigo-400 dark:bg-indigo-500 dark:hover:bg-indigo-400"
          title={t("common.refresh", { defaultValue: "Refresh" })}
        >
          <FaSyncAlt aria-hidden="true" className={refreshing ? "animate-spin" : ""} />
          <span>
            {refreshing
              ? t("mutations.refreshing", { defaultValue: "Refreshing..." })
              : t("common.refresh", { defaultValue: "Refresh" })}
          </span>
        </button>
      </div>
    </header>
  );
};
